import type { EpochSeconds } from "./time";
import type { WorkBlock, WorkDay } from "./workday";

/*
 * Break rule (spec 7.3.1, ADR-0011, ADR-0092), identical to `BreakRule` in the backend and verified
 * with the shared cases in `shared-test-cases/break-rule.json`. The frontend uses it for live
 * warnings. Only declared rest breaks count; standby is time on duty; undeclared breaks and gaps
 * between shifts are unclassified and count neither as break nor as time on duty.
 */

/** A break requirement: more than `workedMoreThanSeconds` of work needs `minBreakSeconds`. */
export interface BreakThreshold {
  readonly workedMoreThanSeconds: number;
  readonly minBreakSeconds: number;
}

/** Parameters of the break rule for one protection level. */
export interface BreakRuleParameters {
  /** Rule identifier reported in findings. */
  readonly ruleId: string;
  /** Thresholds in ascending order. */
  readonly thresholds: readonly BreakThreshold[];
  /** Minimum length of an interruption to count as a rest break. */
  readonly minBreakSegmentSeconds: number;
  /** Maximum working time without a qualifying break. */
  readonly maxContinuousWorkSeconds: number;
}

/** Statutory values of § 4 ArbZG (profile `DE-ArbZG-current`). */
export const arbZgBreakParameters: BreakRuleParameters = {
  ruleId: "ARBZG-BREAK",
  thresholds: [
    { workedMoreThanSeconds: 21_600, minBreakSeconds: 1_800 },
    { workedMoreThanSeconds: 32_400, minBreakSeconds: 2_700 },
  ],
  minBreakSegmentSeconds: 900,
  maxContinuousWorkSeconds: 21_600,
};

/** Statutory values of § 11 JArbSchG (profile `DE-JArbSchG`), stricter in every parameter. */
export const youthBreakParameters: BreakRuleParameters = {
  ruleId: "JARBSCHG-BREAK",
  thresholds: [
    { workedMoreThanSeconds: 16_200, minBreakSeconds: 1_800 },
    { workedMoreThanSeconds: 21_600, minBreakSeconds: 3_600 },
  ],
  minBreakSegmentSeconds: 900,
  maxContinuousWorkSeconds: 16_200,
};

/** Checks of the break rule; `UnclassifiedBreak` is an information, not a violation. */
export type BreakCheck = "TotalBreak" | "ContinuousWork" | "UnclassifiedBreak";

/** A break finding of one working day. */
export interface BreakFinding {
  readonly ruleId: string;
  readonly check: BreakCheck;
  /** `Violation` for closed days, `Warning` while the day is open, `Info` for the hint. */
  readonly severity: "Info" | "Warning" | "Violation";
  readonly actualSeconds: number;
  readonly limitSeconds: number;
}

/** Result of the break analysis of a working day. */
export interface BreakAnalysis {
  /** Sum of declared rest break segments of at least the minimum segment length. */
  readonly qualifyingBreakSeconds: number;
  /** Longest time on duty between two qualifying breaks. */
  readonly longestStretchSeconds: number;
  /** Time on duty since the last qualifying break (the running stretch at the end of the day). */
  readonly currentStretchSeconds: number;
  /** Time on duty that determines the required break: working time plus standby. */
  readonly dutySeconds: number;
  /** Unclassified interruptions of at least the minimum segment length between parts on duty. */
  readonly unclassifiedBreakSeconds: number;
  /** End of the last work block. */
  readonly lastWorkEnd: EpochSeconds | null;
}

/** Part of a working day as the break rule sees it. */
type DayPart = "Work" | "RestBreak" | "Standby" | "Unclassified";

/**
 * Returns the part a block stands for.
 *
 * @param block - The block.
 * @returns The part.
 */
function partOf(block: WorkBlock): DayPart {
  // LEGAL-REVIEW: undeclared breaks are unclassified, standby is time on duty (ADR-0092).
  const part: DayPart = block.kind === "Work" ? "Work" : block.breakQualification;

  return part;
}

/**
 * Analyzes the parts of a day in one pass (O(n)): work blocks, break blocks with their declared
 * qualification and gaps between shifts (unclassified). Adjacent rest break parts form one segment;
 * interruptions count only between two parts on duty (work or standby).
 *
 * @param day - The working day.
 * @param minSegmentSeconds - Minimum length of a qualifying rest break segment.
 * @returns The analysis.
 */
export function analyzeBreaks(day: WorkDay, minSegmentSeconds: number): BreakAnalysis {
  let qualifyingBreakSeconds = 0;
  let unclassifiedBreakSeconds = 0;
  let dutySeconds = 0;
  let currentStretchSeconds = 0;
  let longestStretchSeconds = 0;
  let runPart: DayPart | null = null;
  let runSeconds = 0;
  let pendingQualifying = 0;
  let pendingUnclassified = 0;
  let hasDuty = false;
  let previousEnd: EpochSeconds | null = null;
  let lastWorkEnd: EpochSeconds | null = null;

  const closeRun = (): void => {
    if (runSeconds >= minSegmentSeconds && runPart === "RestBreak") {
      pendingQualifying += runSeconds;
    } else if (runSeconds >= minSegmentSeconds && runPart === "Unclassified") {
      pendingUnclassified += runSeconds;
    }

    runPart = null;
    runSeconds = 0;
  };

  const add = (part: DayPart, seconds: number): void => {
    if (part === "RestBreak" || part === "Unclassified") {
      if (runPart !== part) {
        closeRun();
        runPart = part;
      }

      runSeconds += seconds;
      return;
    }

    closeRun();

    // Interruptions count only between two parts on duty; leading and trailing ones are no breaks.
    if (hasDuty) {
      qualifyingBreakSeconds += pendingQualifying;
      unclassifiedBreakSeconds += pendingUnclassified;
      currentStretchSeconds = pendingQualifying > 0 ? 0 : currentStretchSeconds;
    }

    pendingQualifying = 0;
    pendingUnclassified = 0;
    hasDuty = true;
    dutySeconds += seconds;
    currentStretchSeconds += seconds;
    longestStretchSeconds = Math.max(longestStretchSeconds, currentStretchSeconds);
  };

  for (const block of day.blocks) {
    if (previousEnd !== null && block.start > previousEnd) {
      add("Unclassified", block.start - previousEnd);
    }

    add(partOf(block), block.end - block.start);
    previousEnd = block.end;
    lastWorkEnd = block.kind === "Work" ? block.end : lastWorkEnd;
  }

  const analysis: BreakAnalysis = {
    qualifyingBreakSeconds,
    longestStretchSeconds,
    currentStretchSeconds,
    dutySeconds,
    unclassifiedBreakSeconds,
    lastWorkEnd,
  };

  return analysis;
}

/**
 * Returns the break required for an amount of work: the highest threshold that is exceeded.
 *
 * @param workSeconds - Working time (time on duty).
 * @param thresholds - Thresholds in ascending order.
 * @returns The required break in seconds (0 if none).
 */
export function requiredBreakSeconds(
  workSeconds: number,
  thresholds: readonly BreakThreshold[],
): number {
  let required = 0;

  for (const threshold of thresholds) {
    if (threshold.workedMoreThanSeconds < workSeconds) {
      required = threshold.minBreakSeconds;
    }
  }

  return required;
}

/**
 * Evaluates the break rule for a working day.
 *
 * @param day - The working day.
 * @param parameters - Rule parameters.
 * @returns Findings ordered by check (`ContinuousWork`, `TotalBreak`, then the `UnclassifiedBreak`
 *   hint, which appears only together with one of the others).
 */
export function evaluateBreakRule(day: WorkDay, parameters: BreakRuleParameters): BreakFinding[] {
  const analysis = analyzeBreaks(day, parameters.minBreakSegmentSeconds);
  const severity = day.isClosed ? "Violation" : "Warning";
  const findings: BreakFinding[] = [];

  if (analysis.longestStretchSeconds > parameters.maxContinuousWorkSeconds) {
    findings.push({
      ruleId: parameters.ruleId,
      check: "ContinuousWork",
      severity,
      actualSeconds: analysis.longestStretchSeconds,
      limitSeconds: parameters.maxContinuousWorkSeconds,
    });
  }

  const required = requiredBreakSeconds(analysis.dutySeconds, parameters.thresholds);

  if (required > 0 && analysis.qualifyingBreakSeconds < required) {
    findings.push({
      ruleId: parameters.ruleId,
      check: "TotalBreak",
      severity,
      actualSeconds: analysis.qualifyingBreakSeconds,
      limitSeconds: required,
    });
  }

  if (findings.length > 0 && analysis.unclassifiedBreakSeconds > 0) {
    findings.push({
      ruleId: parameters.ruleId,
      check: "UnclassifiedBreak",
      severity: "Info",
      actualSeconds: analysis.unclassifiedBreakSeconds,
      limitSeconds: 0,
    });
  }

  return findings;
}
